import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Container, Typography, Box, CircularProgress, Chip } from '@mui/material';

// Chart sections
import BeatTheMarket from './sections/BeatTheMarket';
import SectorTreemap from './sections/SectorTreemap';
import SeasonalHeatmap from './sections/SeasonalHeatmap';
import DelayScatter from './sections/DelayScatter';
import PortfolioDonut from './sections/PortfolioDonut';
import TopBottomTraders from './sections/TopBottomTraders';
import TradeSizeDistribution from './sections/TradeSizeDistribution';
import AlphaByState from './sections/AlphaByState';
import CallsVsPuts from './sections/CallsVsPuts';
import TradingTimeline from './sections/TradingTimeline';
import GradeVsFrequency from './sections/GradeVsFrequency';
import DelayTrend from './sections/DelayTrend';
import TopSingleTrades from './sections/TopSingleTrades';
import PartySectorHeatmap from './sections/PartySectorHeatmap';
import SectorRadar from './sections/SectorRadar';
import BiggestTradingDays from './sections/BiggestTradingDays';
import PostBuyTrajectory from './sections/PostBuyTrajectory';
import SkillVsActivity from './sections/SkillVsActivity';
import VolumeVsFedRate from './sections/VolumeVsFedRate';
import SectorVsCPI from './sections/SectorVsCPI';
import TradingVsUnemployment from './sections/TradingVsUnemployment';
import GDPVsSentiment from './sections/GDPVsSentiment';
import RateSensitiveSectors from './sections/RateSensitiveSectors';
import PartyComparison from './sections/PartyComparison';
import TotalProfit from './sections/TotalProfit';
import DisclosureDelay from './sections/DisclosureDelay';
import BiggestTraders from './sections/BiggestTraders';
import MostTradedStocks from './sections/MostTradedStocks';
import ExperienceVsAlpha from './sections/ExperienceVsAlpha';
import BuySellSentiment from './sections/BuySellSentiment';
import VolumeOverTime from './sections/VolumeOverTime';
import NewVsDeparting from './sections/NewVsDeparting';

const NAV_SECTIONS = [
    { id: 'cat-performance', label: 'Performance' },
    { id: 'cat-behavior', label: 'Trading Patterns' },
    { id: 'cat-accountability', label: 'Accountability' },
    { id: 'cat-signals', label: 'Market Signals' },
    { id: 'cat-economic', label: 'Economic Correlations' },
];

function CategoryHeader({ id, title }) {
    return (
        <Typography
            id={id}
            variant="h2"
            sx={{ color: 'text.primary', mt: 5, mb: 2, scrollMarginTop: '80px', fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.01em' }}
        >
            {title}
        </Typography>
    );
}

export default function Analytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('https://api.capitolgains.io/analytics')
            .then(res => res.json())
            .then(d => { setData(d); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Container sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ color: 'text.muted' }}>Crunching 41,000+ trades...</Typography>
            </Container>
        );
    }

    if (!data) {
        return (
            <Container sx={{ pt: 6 }}>
                <Typography sx={{ color: 'text.muted' }}>Analytics data unavailable. Run build_analytics.py to generate.</Typography>
            </Container>
        );
    }

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* Hero */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h1" sx={{ color: 'text.primary', mb: 1 }}>
                        Congressional Trading Analytics
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640, mx: 'auto' }}>
                        Data-driven answers to the questions everyone is asking about stock trading
                        in Congress. {data.total_stock_trades?.toLocaleString()} trades analyzed
                        across {data.total_members} members.
                    </Typography>
                </Box>

                {/* Jump-to navigation */}
                <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                    {NAV_SECTIONS.map(s => (
                        <Chip
                            key={s.id}
                            label={s.label}
                            size="small"
                            onClick={() => scrollTo(s.id)}
                            sx={{
                                cursor: 'pointer',
                                bgcolor: 'transparent',
                                color: 'text.secondary',
                                border: t => '1px solid ' + t.palette.custom.border.subtle,
                                fontSize: '0.75rem',
                                '&:hover': { bgcolor: 'custom.surface.elevated', color: 'text.primary' },
                            }}
                        />
                    ))}
                </Box>

                {/* === PERFORMANCE === */}
                <CategoryHeader id="cat-performance" title="Performance" />
                <BeatTheMarket data={data.beat_the_market} />
                <PartyComparison data={data.party_comparison} />
                <TotalProfit data={data.total_profit} />
                <TopBottomTraders data={data.top_bottom_diverging} />
                <TopSingleTrades data={data.top_single_trades} />
                <PostBuyTrajectory data={data.post_buy_trajectory} />

                {/* === BEHAVIOR === */}
                <CategoryHeader id="cat-behavior" title="Trading Patterns" />
                <SkillVsActivity data={data.skill_vs_activity} />
                <GradeVsFrequency data={data.grade_vs_frequency} />
                <ExperienceVsAlpha data={data.experience_vs_alpha} />
                <AlphaByState data={data.alpha_by_state} />
                <NewVsDeparting data={data.new_vs_departing} />
                <SectorTreemap data={data.sector_treemap} />
                <PortfolioDonut data={data.portfolio_donut} />
                <SectorRadar data={data.sector_radar} />
                <MostTradedStocks data={data.most_traded_stocks} />
                <BiggestTraders data={data.biggest_traders} />
                <TradingTimeline data={data.trading_frequency_timeline} />
                <TradeSizeDistribution data={data.trade_size_distribution} />

                {/* === TIMING & ACCOUNTABILITY === */}
                <CategoryHeader id="cat-accountability" title="Accountability" />
                <DisclosureDelay data={data.disclosure_delay} />
                <DelayScatter data={data.delay_scatter} />
                <DelayTrend data={data.delay_trend} />
                <BiggestTradingDays data={data.biggest_trading_days} />
                <SeasonalHeatmap data={data.seasonal_heatmap} />

                {/* === MARKET SIGNALS === */}
                <CategoryHeader id="cat-signals" title="Market Signals" />
                <BuySellSentiment data={data.buy_sell_sentiment} />
                <VolumeOverTime data={data.volume_over_time} />
                <CallsVsPuts data={data.calls_vs_puts} />
                <PartySectorHeatmap data={data.party_sector_heatmap} />

                {/* === ECONOMIC CORRELATIONS (FRED) === */}
                <CategoryHeader id="cat-economic" title="Economic Correlations" />
                {(data.trade_volume_vs_fed_rate || data.sector_rotation_vs_cpi || data.trading_vs_unemployment || data.gdp_vs_sentiment || data.rate_sensitive_sectors) ? (
                    <>
                        <VolumeVsFedRate data={data.trade_volume_vs_fed_rate} />
                        <SectorVsCPI data={data.sector_rotation_vs_cpi} />
                        <TradingVsUnemployment data={data.trading_vs_unemployment} />
                        <GDPVsSentiment data={data.gdp_vs_sentiment} />
                        <RateSensitiveSectors data={data.rate_sensitive_sectors} />
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" sx={{ color: 'text.muted' }}>
                            Economic data from the Federal Reserve (FRED) is temporarily unavailable. These charts will appear once the data is refreshed.
                        </Typography>
                    </Box>
                )}

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.muted', maxWidth: 600, mx: 'auto', display: 'block', lineHeight: 1.6 }}>
                        All data sourced from mandatory STOCK Act disclosure filings and the Federal Reserve (FRED).
                        Economic data provided by the Federal Reserve Bank of St. Louis.
                        Last updated: {data.generated_at ? new Date(data.generated_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                </Box>
            </motion.div>
        </Container>
    );
}
